'use client'
import { IRange } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { getFormattedDate } from '@/src/utils/date'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DateSelector } from './dateSelector'

interface DateSelectorWrapperProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    isRoundTrip: boolean
    range: IRange
    onDateSelect: (dates: IRange) => void
}

const DateSelectorWrapper: React.FC<DateSelectorWrapperProps> = ({
    open,
    onOpen,
    onClose,
    isRoundTrip,
    range,
    onDateSelect
}) => {
    // State management
    const [activeOnward, setActiveOnward] = useState(true)
    const [activeReturn, setActiveReturn] = useState(false)
    const [localRange, setLocalRange] = useState(range)

    const t = useTranslations('common')
    const routeParams = useParams()
    const locale = (routeParams?.locale as string) || 'en'

    // Format date for display
    const formatDateDisplay = useCallback((date: Date | null) => {
        if (!date) return 'Select Date'
        return getFormattedDate(date, 'DD MMM YYYY')
    }, [])

    // Computed values

    const departureDate = useMemo(() => formatDateDisplay(localRange.from), [localRange.from, formatDateDisplay])

    const returnDate = useMemo(() => formatDateDisplay(localRange.to), [localRange.to, formatDateDisplay])

    const returnTabEnabled = useMemo(() => !!localRange.from, [localRange.from])

    // Sync local range with prop changes
    useEffect(() => {
        setLocalRange(range)
    }, [range])

    // Reset state when opening
    useEffect(() => {
        if (open) {
            setActiveOnward(true)
            setActiveReturn(false)
        }
    }, [open])

    // Handle date click from calendar
    const handleDateClick = useCallback(
        (dates: any) => {
            setLocalRange(dates)
            console.log(dates)
            if (isRoundTrip && dates['to'] && activeReturn) {
                // Both dates selected for round trip - apply and close
                onDateSelect(dates)
            } else if (isRoundTrip && dates['from'] && activeOnward) {
                // Only departure date selected for round trip - switch to return
                setActiveOnward(false)
                setActiveReturn(true)
                // Keep calendar open for return date selection
            } else if (!isRoundTrip) {
                // One way trip completed - apply and close
                onDateSelect(dates)
            }
        },
        [isRoundTrip, activeReturn, activeOnward, onDateSelect, onClose]
    )

    // Handle apply button
    const handleApply = useCallback(() => {
        onDateSelect(localRange)
        onClose()
    }, [localRange, onDateSelect, onClose])

    // Handle departure tab click
    const handleDepartureClick = useCallback(() => {
        setActiveOnward(true)
        setActiveReturn(false)
    }, [])

    // Handle return tab click
    const handleReturnClick = useCallback(() => {
        if (localRange.from) {
            setActiveOnward(false)
            setActiveReturn(true)
        }
    }, [localRange.from])

    // Placeholder for fare calendar direction click
    const directionClick = useCallback((departRange: any) => {
        // getDateRangePrice(departRange) - to be implemented when needed
    }, [])

    return (
        <DateSelector
            isRtl={locale === 'ar'}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            isRoundTrip={isRoundTrip}
            departureDate={departureDate}
            returnDate={returnDate}
            activeOnward={activeOnward}
            activeReturn={activeReturn}
            localRange={localRange}
            returnTabEnabled={returnTabEnabled}
            onDepartureClick={handleDepartureClick}
            onReturnClick={handleReturnClick}
            onDateClick={handleDateClick}
            onApply={handleApply}
            directionClick={directionClick}
            t={t}
        />
    )
}

export { DateSelectorWrapper as DateSelector }
export default DateSelectorWrapper
